import { createAxiosInstanceSetAction } from "./../redux/actions/axios";
import {
  DataProvider,
  GetListParams,
  GetOneParams,
  GetManyParams,
  GetManyReferenceParams,
  UpdateParams,
  UpdateManyParams,
  CreateParams,
  DeleteParams,
  DeleteManyParams,
  GetListResult,
  GetOneResult,
  GetManyResult,
  GetManyReferenceResult,
  UpdateResult,
  UpdateManyResult,
  CreateResult,
  DeleteResult,
  DeleteManyResult,
  Record,
} from "react-admin";
import { Configuration, RecordStatus } from "./service-proxies";
import { AuthManager } from "../auth/Auth";
import Axios, { AxiosInstance } from "axios";
import { serviceProxyFactory } from "./serviceProxyFactory";
import resourceMap from "./resourceMap";
import {
  IResourceMap,
  IResolvedApiResource,
  ChangeStatusParams,
} from "./types";
import apiToDataProviderMap from "./apiToDataProviderMap";
import dataProviderToApiMap from "./dataProviderToApiMap";
import messages from "../i18n/messages";
import { Dispatch } from "react";
import { handleAxiosError } from "./axiosErrorHandler";
import { exclusiveMasterResources } from "./exclusiveMasterResources";

export class ApiManager implements DataProvider {
  private authManager: AuthManager;
  private resourceMap?: IResourceMap;
  private axiosInstance?: AxiosInstance;
  private baseURL: string;
  private searchFields: { [key: string]: Array<string> };

  constructor(authManager: AuthManager, baseURL: string) {
    this.baseURL = baseURL;
    this.authManager = authManager;
    this.searchFields = {};
  }

  [key: string]: any;

  init = (dispatch: Dispatch<any>) => {
    this.axiosInstance = Axios.create({
      baseURL: this.baseURL,
      headers: {
        Accept: "application/json",
        "X-Requested-With": "XMLHttpRequest",
      },
    });
    this.axiosInstance.interceptors.request.use(
      async (config) => {
        try {
          config.headers = {
            ...config.headers,
            ...config.headers.common,
            Authorization:
              config.headers.Authorization ??
              `Bearer ${await this.authManager.getAccessToken()}`,
            "X-Client-TimeZone": Intl.DateTimeFormat().resolvedOptions()
              .timeZone,
          };
        } catch (e) {
          console.warn("Unable to obtain an access token");
        }
        return config;
      },
      (error) => {
        console.error(error);
        return Promise.reject(error);
      }
    );

    this.axiosInstance.interceptors.response.use((response) => {
      if (response.headers["x-prologs-logout"]) {
        this.authManager.logoutFromIdentityProvider({
          redirectBack: true,
        });
      }

      return response;
    });

    // If already authenticated, dispatch the AXIOS_INSTANCE_SET action,
    // else schedule the dispatch when the user is authenticated
    const dispatchAxiosInstanceSetWhenReady = (
      axios: AxiosInstance,
      baseURL: string
    ) => {
      this.authManager
        .checkAuth(null)
        .then(() => {
          dispatch(
            createAxiosInstanceSetAction({ axios: axios, baseURL: baseURL })
          );
        })
        .catch(() => {
          this.authManager.addUserLoadedEvent(() => {
            dispatch(
              createAxiosInstanceSetAction({ axios: axios, baseURL: baseURL })
            );
          });
        });
    };
    dispatchAxiosInstanceSetWhenReady(this.axiosInstance, this.baseURL);
    this.resourceMap = resourceMap(this.baseURL, this.axiosInstance);
  };

  getResourceCrudInterface = (
    resource: any,
    config?: Configuration
  ): IResolvedApiResource | null => {
    if (!this.resourceMap) {
      return null;
    }

    const apiResource = this.resourceMap.getApiResource(resource);
    if (!apiResource) {
      throw new Error(
        `The resource map doesn't contain the requested resource '${resource}'`
      );
    }

    return {
      api: apiResource.api(config),
      apiRouteName: apiResource.apiRouteName,
      isMockResource: apiResource.isMockResource,
    };
  };

  getList = async <RecordType extends Record = Record>(
    resource: string,
    params: GetListParams,
    config?: Configuration,
    options?: any
  ): Promise<GetListResult<RecordType>> => {
    const api = this.getApi(resource, config);
    if (!api) {
      throw new Error(messages.INTERNAL_ERROR);
    }

    try {
      const proxy = serviceProxyFactory(api);
      const apiParams = dataProviderToApiMap.mapGetListParams(
        params,
        this.searchFields[resource]
      );

      // Make sure the original access token is used for exclusive master resources get list calls
      if (exclusiveMasterResources.includes(resource)) {
        options = {
          headers: {
            Authorization: `Bearer ${await this.authManager.getAccessToken({
              ignoreUserOverride: true,
            })}`,
            ...options,
          },
        };
      }

      const result = proxy.getFilteredList(apiParams, options);
      return await apiToDataProviderMap.mapToListResult(result);
    } catch (error) {
      const code = handleAxiosError(error);
      throw new Error(code);
    }
  };

  getOne = async <RecordType extends Record = Record>(
    resource: string,
    params: GetOneParams,
    config?: Configuration
  ): Promise<GetOneResult<RecordType>> => {
    const api = this.getApi(resource, config);
    if (!api) {
      throw new Error(messages.INTERNAL_ERROR);
    }

    try {
      const proxy = serviceProxyFactory(api);
      const result = await proxy.getOneById(`${params.id}`);
      return { data: result.data };
    } catch (error) {
      const code = handleAxiosError(error);
      throw new Error(code);
    }
  };

  getMany = async <RecordType extends Record = Record>(
    resource: string,
    params: GetManyParams,
    config?: Configuration
  ): Promise<GetManyResult<RecordType>> => {
    const api = this.getApi(resource, config);
    if (!api) {
      throw new Error(messages.INTERNAL_ERROR);
    }

    try {
      const proxy = serviceProxyFactory(api);
      const result = await proxy.getMany(params.ids.map((id) => `${id}`));
      return { data: result.data };
    } catch (error) {
      const code = handleAxiosError(error);
      throw new Error(code);
    }
  };

  getManyReference = async <RecordType extends Record = Record>(
    resource: string,
    params: GetManyReferenceParams,
    config?: Configuration
  ): Promise<GetManyReferenceResult<RecordType>> => {
    const api = this.getApi(resource, config);
    if (!api) {
      throw new Error(messages.INTERNAL_ERROR);
    }

    try {
      const proxy = serviceProxyFactory(api);
      const apiParams = dataProviderToApiMap.mapGetManyReferenceParams(params);
      const result = proxy.getFilteredList(apiParams);
      return await apiToDataProviderMap.mapToListResult(result);
    } catch (error) {
      const code = handleAxiosError(error);
      throw new Error(code);
    }
  };

  update = async <RecordType extends Record = Record>(
    resource: string,
    params: UpdateParams,
    config?: Configuration
  ): Promise<UpdateResult<RecordType>> => {
    const api = this.getApi(resource, config);
    if (!api) {
      throw new Error(messages.INTERNAL_ERROR);
    }

    try {
      const proxy = serviceProxyFactory(api);
      const result = await proxy.updateOne(`${params.id}`, params.data);
      return { data: result.data };
    } catch (error) {
      const code = handleAxiosError(error);
      throw new Error(code);
    }
  };

  updateMany = async (
    resource: string,
    params: UpdateManyParams,
    config?: Configuration
  ): Promise<UpdateManyResult> => {
    const api = this.getApi(resource, config);
    if (!api) {
      throw new Error(messages.INTERNAL_ERROR);
    }

    try {
      const proxy = serviceProxyFactory(api);
      const result = await proxy.updateMany({
        ids: params.ids.map((id) => `${id}`),
        data: params.data,
      });
      return { data: result.data };
    } catch (error) {
      const code = handleAxiosError(error);
      throw new Error(code);
    }
  };

  create = async <RecordType extends Record = Record>(
    resource: string,
    params: CreateParams,
    config?: Configuration
  ): Promise<CreateResult<RecordType>> => {
    if (params.data.__endpointOverride && this.axiosInstance) {
      const { __endpointOverride, ...data } = params.data;
      return await this.axiosInstance.post(__endpointOverride, data);
    }

    const api = this.getApi(resource, config);
    if (!api) {
      throw new Error(messages.INTERNAL_ERROR);
    }

    try {
      const proxy = serviceProxyFactory(api);
      const result = await proxy.createOne(params.data);
      return { data: result.data };
    } catch (error) {
      const code = handleAxiosError(error);
      throw new Error(code);
    }
  };

  delete = async <RecordType extends Record = Record>(
    resource: string,
    params: DeleteParams,
    config?: Configuration
  ): Promise<DeleteResult<RecordType>> => {
    const api = this.getApi(resource, config);
    if (!api) {
      throw new Error(messages.INTERNAL_ERROR);
    }

    try {
      const proxy = serviceProxyFactory(api);
      await proxy.deleteOne(`${params.id}`);
      return { data: params.previousData as RecordType };
    } catch (error) {
      const code = handleAxiosError(error);
      throw new Error(code);
    }
  };

  deleteMany = async (
    resource: string,
    params: DeleteManyParams,
    config?: Configuration
  ): Promise<DeleteManyResult> => {
    const api = this.getApi(resource, config);
    if (!api) {
      throw new Error(messages.INTERNAL_ERROR);
    }

    try {
      const proxy = serviceProxyFactory(api);
      const result = await proxy.deleteMany(params.ids.map((id) => `${id}`));
      return { data: result.data };
    } catch (error) {
      const code = handleAxiosError(error);
      throw new Error(code);
    }
  };

  changeStatus = async (
    resource: string,
    params: ChangeStatusParams,
    config?: Configuration
  ): Promise<Record & { status: RecordStatus }> => {
    const api = this.getApi(resource, config);
    if (!api) {
      throw new Error(messages.INTERNAL_ERROR);
    }

    try {
      const proxy = serviceProxyFactory(api);
      if (!proxy.changeStatus) {
        return Promise.reject(
          "This entity doesn't support the changeStatus method"
        );
      }
      const result = await proxy.changeStatus(params.id, params.status);
      return result.data;
    } catch (error) {
      const code = handleAxiosError(error);
      throw new Error(code);
    }
  };

  setSearchFields = (resource: string, fields: Array<string>) => {
    this.searchFields[resource] = [...fields];
  };

  private getApi = (
    resource: string,
    config?: Configuration
  ): IResolvedApiResource | null => {
    let api: IResolvedApiResource | null = null;
    try {
      api = this.getResourceCrudInterface(resource, config);
      if (!api) {
        throw new Error(messages.API_CALL_INIT_FIRST);
      }
    } catch (error) {
      console.error(error);
    }

    return api;
  };
}
